import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import { SitStatSortableGrid } from './SitStatSortableGrid';
import { SitStatMapLayersWidget } from './SitStatMapLayersWidget';
import { SitStatActionsWidget } from './SitStatActionsWidget';
import { SitStatNotificationsWidget } from './SitStatNotificationsWidget';
import { SitStatEventsWidget } from './SitStatEventsWidget';
import { SitStatIncidentsWidget } from './SitStatIncidentsWidget';
import { SitStatAorsWidget } from './SitStatAorsWidget';
import { SitStatAssetsWidget } from './SitStatAssetsWidget';
import { SitStatAorScopeProvider } from './SitStatAorScopeContext';
import { SitStatAorFilterBar } from './SitStatAorFilterBar';
import { LayerSidebarPanelProvider } from '../LayersTab/LayerSidebarPanel';
import {
  isAorSitStatTemplateId,
  listSitStatTemplates,
  loadActiveSitStatTemplateId,
  loadSitStatUiState,
  normalizeLayout,
  resolveSitStatStorageKey,
  saveActiveSitStatTemplateId,
  saveSitStatUiState,
  saveUserSitStatTemplates,
  updateUserSitStatTemplateState,
  type SitStatUiState,
} from './sitstat-layout-storage';
import {
  SITSTAT_AOR_PARAM,
  SITSTAT_TEMPLATE_PARAM,
} from './sitstat-navigation';
import type { SitStatTemplate } from './sitstat-template-types';
import type { SitStatLayout, SitStatWidgetId } from './sitstat-widget-types';

export function renderSitStatWidget(id: SitStatWidgetId): ReactNode {
  switch (id) {
    case 'notifications':
      return <SitStatNotificationsWidget />;
    case 'map-layers':
      return <SitStatMapLayersWidget />;
    case 'events':
      return <SitStatEventsWidget />;
    case 'actions':
      return <SitStatActionsWidget />;
    case 'aors':
      return <SitStatAorsWidget />;
    case 'incidents':
      return <SitStatIncidentsWidget />;
    case 'assets':
      return <SitStatAssetsWidget />;
    default:
      return null;
  }
}

type SitStatWorkspaceProps = {
  mapSlot: ReactNode;
  onExitSitStat: () => void;
};

export function SitStatWorkspace({
  mapSlot,
  onExitSitStat,
}: SitStatWorkspaceProps) {
  const groupGuid = useAppSelector(selectGroupGuid);
  const storageKey = useMemo(
    () => resolveSitStatStorageKey(groupGuid ?? null),
    [groupGuid]
  );

  const [uiState, setUiState] = useState<SitStatUiState>(() =>
    loadSitStatUiState(storageKey)
  );
  const [templates, setTemplates] = useState<SitStatTemplate[]>(() =>
    listSitStatTemplates(storageKey)
  );
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(() =>
    loadActiveSitStatTemplateId(storageKey)
  );

  useEffect(() => {
    setUiState(loadSitStatUiState(storageKey));
    setTemplates(listSitStatTemplates(storageKey));
    setActiveTemplateId(loadActiveSitStatTemplateId(storageKey));
  }, [storageKey]);

  const persistUi = useCallback(
    (updater: (prev: SitStatUiState) => SitStatUiState) => {
      setUiState((prev) => {
        const next = updater(prev);
        saveSitStatUiState(storageKey, next);
        return next;
      });
    },
    [storageKey]
  );

  // AOR templates follow the user's edits; any other template is detached on edit.
  const applyUiChange = useCallback(
    (updater: (prev: SitStatUiState) => SitStatUiState) => {
      persistUi((prev) => {
        const next = updater(prev);
        if (activeTemplateId && isAorSitStatTemplateId(activeTemplateId)) {
          const user = updateUserSitStatTemplateState(
            storageKey,
            activeTemplateId,
            next
          );
          setTemplates((current) => [
            ...current.filter((entry) => entry.isSystem),
            ...user,
          ]);
        }
        return next;
      });
    },
    [activeTemplateId, persistUi, storageKey]
  );

  const handleLayoutChange = useCallback(
    (layout: SitStatLayout) => {
      applyUiChange((prev) => ({
        ...prev,
        layout: normalizeLayout(layout),
      }));
      if (isAorSitStatTemplateId(activeTemplateId)) return;
      setActiveTemplateId(null);
      saveActiveSitStatTemplateId(storageKey, null);
    },
    [activeTemplateId, applyUiChange, storageKey]
  );

  const handleLeftCollapsedChange = useCallback(
    (leftCollapsed: boolean) => {
      applyUiChange((prev) => ({ ...prev, leftCollapsed }));
    },
    [applyUiChange]
  );

  const handleRightCollapsedChange = useCallback(
    (rightCollapsed: boolean) => {
      applyUiChange((prev) => ({ ...prev, rightCollapsed }));
    },
    [applyUiChange]
  );

  const handleLoadTemplate = useCallback(
    (template: SitStatTemplate) => {
      persistUi(() => ({
        layout: normalizeLayout(template.layout),
        leftCollapsed: template.uiState.leftCollapsed === true,
        rightCollapsed: template.uiState.rightCollapsed === true,
      }));
      setActiveTemplateId(template.id);
      saveActiveSitStatTemplateId(storageKey, template.id);
    },
    [persistUi, storageKey]
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTemplateId = searchParams.get(SITSTAT_TEMPLATE_PARAM);
  const requestedAorId = searchParams.get(SITSTAT_AOR_PARAM);
  const [selectedAorId, setSelectedAorId] = useState<string | null>(
    () => requestedAorId || null
  );

  // Launch params are one-shot. The ref keeps a re-render that lands before the
  // URL cleanup commits from loading (and navigating) again, which would race
  // Exit SitStat's own `openTab` change.
  const consumedLaunchRef = useRef<string | null>(null);
  useEffect(() => {
    if (requestedTemplateId === null && requestedAorId === null) {
      consumedLaunchRef.current = null;
      return;
    }
    if (!groupGuid) return;
    const launchKey = `${requestedTemplateId ?? ''}|${requestedAorId ?? ''}`;
    if (consumedLaunchRef.current === launchKey) return;
    consumedLaunchRef.current = launchKey;

    if (requestedTemplateId) {
      const available = listSitStatTemplates(storageKey);
      const template = available.find(
        (entry) => entry.id === requestedTemplateId
      );
      if (template) {
        setTemplates(available);
        handleLoadTemplate(template);
      }
    }
    if (requestedAorId !== null) {
      setSelectedAorId(requestedAorId || null);
    }
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        next.delete(SITSTAT_TEMPLATE_PARAM);
        next.delete(SITSTAT_AOR_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [
    groupGuid,
    handleLoadTemplate,
    requestedAorId,
    requestedTemplateId,
    setSearchParams,
    storageKey,
  ]);

  const handleSaveTemplate = useCallback(
    (name: string) => {
      const id = `user:${Date.now()}`;
      setUiState((prev) => {
        const template: SitStatTemplate = {
          id,
          slug: id,
          name,
          layout: normalizeLayout(prev.layout),
          uiState: {
            leftCollapsed: prev.leftCollapsed,
            rightCollapsed: prev.rightCollapsed,
          },
          isSystem: false,
        };
        setTemplates((current) => {
          const system = current.filter((entry) => entry.isSystem);
          const user = current.filter((entry) => !entry.isSystem);
          const ordered = [...system, ...user, template];
          saveUserSitStatTemplates(storageKey, ordered);
          return ordered;
        });
        setActiveTemplateId(id);
        saveActiveSitStatTemplateId(storageKey, id);
        return prev;
      });
    },
    [storageKey]
  );

  return (
    // `dark` opts SitStat shadcn primitives into the dark token set (IAP pattern).
    <div
      className="dark tw:flex tw:h-full tw:min-h-0 tw:w-full tw:flex-col"
      data-tour="sitstat-workspace"
    >
      <SitStatAorScopeProvider
        selectedAorId={selectedAorId}
        onSelectedAorIdChange={setSelectedAorId}
      >
        <SitStatAorFilterBar />
        {/* Map Layers widget reaches for this panel context to add and edit layers. */}
        <LayerSidebarPanelProvider>
          <div className="tw:min-h-0 tw:flex-1">
            <SitStatSortableGrid
              layout={uiState.layout}
              onLayoutChange={handleLayoutChange}
              leftCollapsed={uiState.leftCollapsed}
              rightCollapsed={uiState.rightCollapsed}
              onLeftCollapsedChange={handleLeftCollapsedChange}
              onRightCollapsedChange={handleRightCollapsedChange}
              onExitSitStat={onExitSitStat}
              renderWidget={renderSitStatWidget}
              templates={templates}
              activeTemplateId={activeTemplateId}
              onLoadTemplate={handleLoadTemplate}
              onSaveTemplate={handleSaveTemplate}
              mapSlot={mapSlot}
            />
          </div>
        </LayerSidebarPanelProvider>
      </SitStatAorScopeProvider>
    </div>
  );
}
